import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

/**
 * Creates and starts the post-onboarding product tour.
 * Call startProductTour(router) from App.vue after the onboarding flow completes.
 */
export function startProductTour(router) {
  const tour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'bs-tour-step',
      modalOverlayOpeningPadding: 8,
      modalOverlayOpeningRadius: 8,
    },
  });

  // ─── Helper ────────────────────────────────────────────────────────────────

  /**
   * Navigate to a route, then wait for an element to appear before resolving.
   */
  function navigateAndWait(routePath, selector, timeout = 4000) {
    return new Promise((resolve) => {
      router.push(routePath).then(() => {
        const start = Date.now();
        const interval = setInterval(() => {
          const el = document.querySelector(selector);
          if (el || Date.now() - start > timeout) {
            clearInterval(interval);
            resolve(el);
          }
        }, 100);
      });
    });
  }

  // ─── Steps ─────────────────────────────────────────────────────────────────

  // 1. Welcome
  tour.addStep({
    id: 'welcome',
    title: '👋 Welcome to BookedSolid!',
    text: "Your setup is complete. Let's take a quick tour so you know where everything lives.",
    buttons: [
      { text: 'Skip tour', action: tour.cancel, secondary: true },
      {
        text: "Let's go →",
        action: async () => {
          await navigateAndWait('/calls', '.transcripts');
          tour.next();
        },
      },
    ],
  });

  // 2. Calls page
  tour.addStep({
    id: 'calls-page',
    title: '📞 Call Transcripts',
    text: `This is where every call your AI receptionist handles will appear — complete with a full transcript, summary, and the actions taken. Once you start receiving calls, they'll show up here.`,
    attachTo: { element: '.transcripts', on: 'right' },
    buttons: [
      {
        text: '← Back', secondary: true,
        action: () => { tour.back(); },
      },
      {
        text: 'Next →',
        action: async () => {
          await navigateAndWait('/messages', '.ai');
          tour.next();
        },
      },
    ],
  });

  // 3. Messages page
  tour.addStep({
    id: 'messages-page',
    title: '💬 Messages',
    text: `This is where SMS, WhatsApp, email, and website widget conversations will appear. It's empty for now, but as soon as a patient messages your number it'll show up here — your AI will handle replies automatically.`,
    attachTo: { element: '.conversations', on: 'right' },
    buttons: [
      {
        text: '← Back', secondary: true,
        action: async () => {
          await navigateAndWait('/calls', '.transcripts');
          tour.back();
        },
      },
      {
        text: 'Next →',
        action: async () => {
          await navigateAndWait('/channels', '.channels');
          tour.next();
        },
      },
    ],
  });

  // 4. Channels — phone numbers list
  tour.addStep({
    id: 'channels-phone-numbers',
    title: '📱 Your Phone Number',
    text: "Your dedicated AI phone number will appear here once it's been provisioned (usually within 24 hours). You can manage multiple numbers, assign them to different clinic locations, and add SMS support from this page.",
    attachTo: { element: 'section', on: 'bottom' },
    buttons: [
      {
        text: '← Back', secondary: true,
        action: async () => {
          await navigateAndWait('/messages', '.ai');
          tour.back();
        },
      },
      {
        text: 'Next →',
        action: async () => {
          await navigateAndWait('/clinic', '.clinic');
          tour.next();
        },
      },
    ],
  });

  // 5. Clinic Setup — PMS data
  tour.addStep({
    id: 'clinic-setup',
    title: '🏥 Clinic Setup',
    text: "Your locations, practitioners, and appointment types from your practice management system will be pulled through here. The AI will automatically train itself on this information to book the right appointments with the right practitioners.",
    attachTo: { element: '.container', on: 'bottom' },
    buttons: [
      {
        text: '← Back', secondary: true,
        action: async () => {
          await navigateAndWait('/channels', '.channels');
          tour.back();
        },
      },
      {
        text: 'Next →',
        action: async () => {
          await navigateAndWait('/settings', '.settings');
          tour.next();
        },
      },
    ],
  });

  // 6. Settings — Account Setup section
  tour.addStep({
    id: 'settings-account',
    title: '⚙️ Account Settings',
    text: 'Here you can update your business name, notifications email, timezone, and which patient details the AI collects at booking.',
    attachTo: {
      element: '.collapsible-section:nth-of-type(1)',
      on: 'bottom',
    },
    buttons: [
      {
        text: '← Back', secondary: true,
        action: async () => {
          await navigateAndWait('/clinic', '.clinic');
          tour.back();
        },
      },
      { text: 'Next →', action: tour.next },
    ],
  });

  // 7. Settings — Call Handling section (voice accent lives here)
  tour.addStep({
    id: 'settings-call-handling',
    title: '🎙️ Call Handling & Voice',
    text: "This section controls how your AI handles calls — including the voice accent, call greeting, escalation/forwarding behaviour, and office hours.",
    attachTo: {
      element: '.collapsible-section:nth-of-type(2)',
      on: 'bottom',
    },
    buttons: [
      { text: '← Back', secondary: true, action: tour.back },
      { text: 'Next →', action: tour.next },
    ],
  });

  // 8. Settings — Message Handling
  tour.addStep({
    id: 'settings-messages',
    title: '✉️ Message Handling',
    text: 'Control how the AI handles inbound messages — auto-reply behaviour, the channels it responds on, and message tone.',
    attachTo: {
      element: '.collapsible-section:nth-of-type(3)',
      on: 'bottom',
    },
    buttons: [
      { text: '← Back', secondary: true, action: tour.back },
      { text: 'Next →', action: tour.next },
    ],
  });

  // 9. Settings — Notifications
  tour.addStep({
    id: 'settings-notifications',
    title: '🔔 Notifications',
    text: 'Configure which email notifications you receive — booking confirmations, cancellations, call transcripts, and more. Make sure your notifications email is set so nothing gets missed.',
    attachTo: {
      element: '.collapsible-section:nth-of-type(4)',
      on: 'bottom',
    },
    buttons: [
      { text: '← Back', secondary: true, action: tour.back },
      { text: 'Finish tour 🎉', action: tour.next },
    ],
  });

  // 10. Done
  tour.addStep({
    id: 'done',
    title: "🎉 You're all set!",
    text: `That's the full tour. Our team will have everything live within 24 hours — you'll get an email when your number is ready. In the meantime, feel free to explore and tweak any settings.`,
    buttons: [
      {
        text: 'Close',
        action: () => {
          tour.complete();
        },
      },
    ],
  });

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  tour.on('complete', () => {
    localStorage.setItem('productTourCompleted', 'true');
  });

  tour.on('cancel', () => {
    localStorage.setItem('productTourCompleted', 'true');
  });

  tour.start();
  return tour;
}
